import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "@/lib/axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  AlertTriangle,
  Check,
  BarChart3,
  Activity,
  ShieldCheck,
  Key,
  FileText,
  CreditCard,
  Zap,
  Mic2,
  Clock
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

interface ApiConfig {
  apiKey: string;
  dailyLimit: number;
  monthlyLimit: number;
  enabled: boolean;
  lastUpdated: string;
}

interface ApiUsageLog {
  date: string;
  endpoint: string;
  requestSize: number;
  responseSize: number;
  status: string;
  duration: number;
  user: string;
}

const ApiSettings = () => {
  const [activeTab, setActiveTab] = useState<string>("settings");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // API Ayarları sorgusu (API anahtarını da içeren tam ayarlar)
  const { data: apiConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["elevenlabs-api-config"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/settings/full");
      return data;
    },
  });

  // API Kullanım İstatistikleri sorgusu
  const { data: apiUsageData, isLoading: isLoadingUsage } = useQuery({
    queryKey: ["elevenlabs-api-usage"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/settings/api-usage");
      return data;
    },
    enabled: activeTab === "usage",
    refetchInterval: 30000, // Her 30 saniyede bir yenile (aktif sekmede)
  });

  // API Kullanım Logları sorgusu
  const { data: apiLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: ["elevenlabs-api-logs"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/settings/logs");
      return data;
    },
    enabled: activeTab === "logs",
    refetchInterval: 60000, // Her 60 saniyede bir yenile (aktif sekmede)
  });

  // ElevenLabs Abonelik Bilgileri sorgusu
  const { data: subscriptionData, isLoading: isLoadingSubscription, refetch: refetchSubscription } = useQuery({
    queryKey: ["elevenlabs-subscription"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/settings/subscription");
      return data;
    },
    enabled: activeTab === "subscription",
    refetchInterval: 60000,
  });

  // Ayarları güncelleme mutasyonu
  const updateConfig = useMutation({
    mutationFn: async (updatedConfig: Partial<ApiConfig>) => {
      // Tüm ayarları güncellemek için birden fazla istek yapacağız
      const requests = [];
      
      // 1. API anahtarını güncelle
      if (updatedConfig.apiKey) {
        requests.push(
          axios.put("/ai-announcements/settings/api-key", { 
            apiKey: updatedConfig.apiKey 
          })
        );
      }
      
      // 2. Limitleri güncelle
      requests.push(
        axios.put("/ai-announcements/settings/limits", { 
          dailyLimit: updatedConfig.dailyLimit, // Backend'in beklediği parametre ismi
          monthlyLimit: updatedConfig.monthlyLimit // Backend'in beklediği parametre ismi
        })
      );
      
      // 3. API erişimini etkinleştir/devre dışı bırak (allowBackgroundMusic)
      if (updatedConfig.enabled !== undefined) {
        // Backend hem allowBackgroundMusic hem de en az bir limit değeri (dailyLimit veya monthlyLimit) bekliyor
        // Bu yüzden, sadece API erişimini değiştirdiğimizde de mevcut limit değerlerini gönderiyoruz
        requests.push(
          axios.put("/ai-announcements/settings/limits", {
            allowBackgroundMusic: updatedConfig.enabled,
            // Mevcut limit değerlerini gönder (en az birini göndermek zorunlu)
            dailyLimit: updatedConfig.dailyLimit !== undefined ? updatedConfig.dailyLimit : formData.dailyLimit,
            monthlyLimit: updatedConfig.monthlyLimit !== undefined ? updatedConfig.monthlyLimit : formData.monthlyLimit
          })
        );
      }
      
      // Tüm istekleri bir arada gönder
      await Promise.all(requests);
      
      return { message: "Ayarlar başarıyla güncellendi" };
    },
    onSuccess: (response: any) => {
      // Güncellenmiş ayarların bir özeti
      // Backend cevabı { message, updated } formatında
      const updatedSettings = response?.updated || {};
      const updatedValues = [];
      
      // Günlük limit
      if (updatedSettings.maxDailyUsage !== undefined) {
        updatedValues.push(`Günlük Limit: ${updatedSettings.maxDailyUsage || 0}`);
      }
      
      // Aylık limit
      if (updatedSettings.maxPremiumDailyUsage !== undefined) {
        updatedValues.push(`Aylık Limit: ${updatedSettings.maxPremiumDailyUsage || 0}`);
      }
      
      // API erişimi
      if (updatedSettings.allowBackgroundMusic !== undefined) {
        updatedValues.push(`API Erişimi: ${updatedSettings.allowBackgroundMusic ? 'Etkin' : 'Devre Dışı'}`);
      }
      
      // Daha görünür ve ayrıntılı bir bildirim göster
      toast({
        title: "Ayarlar Başarıyla Güncellendi",
        description: (
          <div className="space-y-1 mt-2">
            <p>ElevenLabs API ayarları başarıyla kaydedildi.</p>
            {updatedValues.length > 0 && (
              <div className="text-xs bg-green-50 p-2 rounded border border-green-100 mt-1">
                <p className="font-semibold mb-1">Güncellenmiş Ayarlar:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {updatedValues.map((value, index) => (
                    <li key={index}>{value}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ),
        duration: 5000, // Bildirimi biraz daha uzun göster
      });
      
      // Verileri yenile
      queryClient.invalidateQueries({ queryKey: ["elevenlabs-api-config"] });
    },
    onError: (_error) => {
      toast({
        title: "Hata",
        description: "Ayarlar kaydedilirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error("Ayarlar kaydedilirken hata:", _error);
    },
  });

  // API Anahtarı Test etme
  const [testSuccess, setTestSuccess] = useState(false);
  const [testError, setTestError] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // API anahtarını maskele: ilk 4 ve son 4 karakteri göster, geri kalanı yıldızla
  const maskApiKey = (key: string) => {
    if (!key) return '';
    if (key.length <= 8) return key; // Çok kısa anahtarları maskeleme
    
    const firstFour = key.substring(0, 4);
    const lastFour = key.substring(key.length - 4);
    
    // Sabit sayıda yıldız kullan - daha temiz görünüm için
    return `${firstFour}${'•'.repeat(16)}${lastFour}`;
  };
  
  // Maskelenmiş API anahtarı
  const [maskedApiKey, setMaskedApiKey] = useState<string>('');
  
  const testApiKey = useMutation({
    mutationFn: async (apiKey: string) => {
      const { data } = await axios.post("/ai-announcements/settings/test-api-key", { apiKey });
      return data;
    },
    onSuccess: (data) => {
      setTestSuccess(true);
      setTestError(false);
      toast({
        title: "API Anahtarı Geçerli",
        description: `ElevenLabs API bağlantısı başarılı. ${data.voiceCount} ses modeli erişilebilir.`,
      });
      
      // 3 saniye sonra başarı durumunu sıfırla
      setTimeout(() => {
        setTestSuccess(false);
      }, 3000);
    },
    onError: () => {
      setTestSuccess(false);
      setTestError(true);
      toast({
        title: "API Anahtarı Geçersiz",
        description: "ElevenLabs API ile bağlantı kurulamadı. Lütfen anahtarı kontrol edin.",
        variant: "destructive",
      });
      
      // 3 saniye sonra hata durumunu sıfırla
      setTimeout(() => {
        setTestError(false);
      }, 3000);
    },
  });

  // Form state
  const [formData, setFormData] = useState<ApiConfig>({
    apiKey: "",
    dailyLimit: 0,
    monthlyLimit: 0,
    enabled: false,
    lastUpdated: "",
  });

  // API Config geldiğinde form state'i güncelle
  useEffect(() => {
    if (apiConfig) {
      console.log("API Config yüklendi:", apiConfig);
      
      // ElevenLabs API anahtarını doğru şekilde al
      const apiKeyValue = apiConfig.elevenlabsApiKey || "";
      console.log("API Anahtarı:", apiKeyValue);
      
      // Form verisini api config ile güncelle
      setFormData({
        apiKey: apiKeyValue,
        dailyLimit: apiConfig.maxDailyUsage || 0,
        monthlyLimit: apiConfig.maxPremiumDailyUsage || 0,
        enabled: apiConfig.allowBackgroundMusic || false,
        lastUpdated: apiConfig.updatedAt || "",
      });
      
      // API anahtarını maskele
      if (apiKeyValue) {
        const masked = maskApiKey(apiKeyValue);
        console.log("Maskelenmiş API Anahtarı:", masked);
        setMaskedApiKey(masked);
      }
    }
  }, [apiConfig]);

  const handleSaveConfig = () => {
    updateConfig.mutate(formData);
  };

  const handleTestApi = () => {
    testApiKey.mutate(formData.apiKey);
  };

  // Logların durum sınıfına göre stil dönüştürme
  const getStatusBadge = (success: boolean) => {
    return success ? (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <Check className="mr-1 h-3 w-3" /> Başarılı
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <AlertTriangle className="mr-1 h-3 w-3" /> Başarısız
      </Badge>
    );
  };
  
  // Endpoint ismini dönüştürme
  const getEndpointName = (endpoint: string) => {
    const endpointMap: Record<string, string> = {
      'text-to-speech': 'Metin-Konuşma',
      'voices': 'Ses Modelleri',
      'user': 'Kullanıcı Bilgileri',
      'models': 'Modeller',
      'other': 'Diğer'
    };
    
    return endpointMap[endpoint] || endpoint;
  };

  // API kullanım istatistikleri
  const apiUsageStats = {
    dailyUsage: 0,
    monthlyUsage: 0,
    averageResponseTime: 0,
    successRate: 0,
  };

  if (apiLogs && apiLogs.length > 0) {
    // Günlük kullanım
    const today = new Date().toISOString().split('T')[0];
    apiUsageStats.dailyUsage = apiLogs.filter((log: ApiUsageLog) => 
      log.date.startsWith(today)).length;
    
    // Aylık kullanım
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    apiUsageStats.monthlyUsage = apiLogs.filter((log: ApiUsageLog) => 
      log.date.startsWith(currentMonth)).length;
    
    // Ortalama yanıt süresi
    const totalDuration = apiLogs.reduce((sum: number, log: ApiUsageLog) => sum + log.duration, 0);
    apiUsageStats.averageResponseTime = totalDuration / apiLogs.length;
    
    // Başarı oranı
    const successfulCalls = apiLogs.filter((log: ApiUsageLog) => log.status === "success").length;
    apiUsageStats.successRate = (successfulCalls / apiLogs.length) * 100;
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">ElevenLabs API Yönetimi</h1>
          <p className="text-muted-foreground">
            AI Anons sistemi için ElevenLabs API yapılandırması ve izleme
          </p>
        </div>
      </div>

      <Tabs defaultValue="settings" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="settings">
            <Key className="w-4 h-4 mr-2" />
            API Ayarları
          </TabsTrigger>
          <TabsTrigger value="usage">
            <BarChart3 className="w-4 h-4 mr-2" />
            Kullanım Analizi
          </TabsTrigger>
          <TabsTrigger value="logs">
            <Activity className="w-4 h-4 mr-2" />
            API Logları
          </TabsTrigger>
          <TabsTrigger value="subscription">
            <CreditCard className="w-4 h-4 mr-2" />
            Abonelik
          </TabsTrigger>
        </TabsList>

        {/* API Ayarları Bölümü */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>API Yapılandırması</CardTitle>
              <CardDescription>
                ElevenLabs API ayarlarını yapılandırın ve kullanım limitlerini belirleyin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingConfig ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Önemli</AlertTitle>
                    <AlertDescription>
                      API anahtarı güvenli bir şekilde saklanır ve yalnızca backend 
                      tarafından kullanılır. Bu anahtar hiçbir zaman client tarafına gönderilmez.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="apiKey">ElevenLabs API Anahtarı</Label>
                      <div className="flex items-center space-x-2 mt-1.5">
                        <div className="relative flex-1">
                          {!showApiKey && formData.apiKey ? (
                            // Maskelenmiş görünüm - kendi özel maskelememizi gösterir
                            <div className="relative">
                              <Input
                                id="apiKey" 
                                type="text" 
                                value={maskedApiKey}
                                readOnly
                                className="pr-10 bg-muted cursor-default text-muted-foreground font-mono"
                              />
                              {/* Maskeleme etiketi kaldırıldı */}
                            </div>
                          ) : (
                            // Normal input - API anahtarını düzenleme veya gösterme
                            <Input
                              id="apiKey"
                              type="text"
                              value={formData.apiKey}
                              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                              placeholder="ElevenLabs API Anahtarı"
                              className="pr-10"
                            />
                          )}
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="icon"
                            className="absolute top-0 right-0 h-full px-3"
                            onClick={() => setShowApiKey(!showApiKey)}
                            title={showApiKey ? "API Anahtarını Gizle" : "API Anahtarını Göster"}
                          >
                            {showApiKey ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        
                        <Button
                          variant={testError ? "destructive" : "outline"}
                          onClick={handleTestApi}
                          disabled={!formData.apiKey || testApiKey.isPending}
                          className={`min-w-[100px] ${testSuccess ? "bg-green-100 text-green-700 hover:bg-green-200 border-green-500" : 
                                    testError ? "bg-red-100 text-red-700 hover:bg-red-200 border-red-500" : ""}`}
                        >
                          {testApiKey.isPending ? (
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          ) : testSuccess ? (
                            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                          ) : testError ? (
                            <AlertCircle className="mr-2 h-4 w-4 text-red-600" />
                          ) : (
                            <ShieldCheck className="mr-2 h-4 w-4" />
                          )}
                          {testSuccess ? "Başarılı" : testError ? "Geçersiz" : "Test Et"}
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        ElevenLabs hesabınızdan alınan API anahtarı
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="dailyLimit">Günlük Limit</Label>
                      <Input
                        id="dailyLimit"
                        type="number"
                        value={formData.dailyLimit}
                        onChange={(e) => setFormData({ ...formData, dailyLimit: parseInt(e.target.value) })}
                        placeholder="Günlük istek limiti"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Günlük maksimum API isteği sayısı (0 = limitsiz)
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="monthlyLimit">Aylık Limit</Label>
                      <Input
                        id="monthlyLimit"
                        type="number"
                        value={formData.monthlyLimit}
                        onChange={(e) => setFormData({ ...formData, monthlyLimit: parseInt(e.target.value) })}
                        placeholder="Aylık istek limiti"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Aylık maksimum API isteği sayısı (0 = limitsiz)
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="apiEnabled"
                        checked={formData.enabled}
                        onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                      />
                      <Label htmlFor="apiEnabled">API Erişimini Etkinleştir</Label>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <div className="text-sm text-muted-foreground">
                {apiConfig?.lastUpdated ? (
                  <span>Son Güncelleme: {new Date(apiConfig.lastUpdated).toLocaleString("tr-TR")}</span>
                ) : null}
              </div>
              <Button onClick={handleSaveConfig} disabled={updateConfig.isPending || isLoadingConfig}>
                {updateConfig.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Ayarları Kaydet
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Kullanım Analizi Bölümü */}
        <TabsContent value="usage">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Günlük Kullanım</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold">
                    {apiUsageData?.stats?.dailyUsage || 0}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.dailyLimit > 0 ? `Limit: ${formData.dailyLimit}` : "Limitsiz"}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Aylık Kullanım</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold">
                    {apiUsageData?.stats?.monthlyUsage || 0}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.monthlyLimit > 0 ? `Limit: ${formData.monthlyLimit}` : "Limitsiz"}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Ortalama Yanıt Süresi</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <div className="text-2xl font-bold">
                    {(apiUsageData?.stats?.responseTime || 0).toFixed(2)} ms
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Son 100 istek ortalaması
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Başarı Oranı</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-bold">
                    %{apiUsageData?.stats?.successRate || 0}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Başarılı/Tüm istekler
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>API Kullanım Trendi</CardTitle>
              <CardDescription>
                Son 30 gün içindeki API kullanım istatistikleri
              </CardDescription>
            </CardHeader>
            <CardContent className="pl-2">
              <div className="h-[300px] w-full">
                {isLoadingUsage ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <Skeleton className="h-[250px] w-full" />
                  </div>
                ) : apiUsageData?.trend && apiUsageData.trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={apiUsageData.trend}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getDate()}/${date.getMonth() + 1}`;
                        }}
                      />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip 
                        formatter={(value, name) => [
                          Number(value).toLocaleString('tr-TR'), 
                          name === 'count' ? 'API İstek Sayısı' : 
                          name === 'characters' ? 'Karakter Sayısı' : 'Yanıt Süresi (ms)'
                        ]}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          return date.toLocaleDateString('tr-TR');
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#8884d8" 
                        fillOpacity={1} 
                        fill="url(#colorCount)" 
                        name="API İstek Sayısı"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <p className="text-sm text-muted-foreground">Henüz kullanım verisi bulunmuyor</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Abonelik Bilgileri Bölümü */}
        <TabsContent value="subscription">
          <div className="space-y-6">
            {/* Üst Kartlar */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Plan Bilgisi */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-purple-500" />
                    Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSubscription ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold capitalize">
                        {subscriptionData?.tier || "—"}
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          subscriptionData?.status === "active"
                            ? "bg-green-50 text-green-700 border-green-200 mt-1"
                            : "bg-yellow-50 text-yellow-700 border-yellow-200 mt-1"
                        }
                      >
                        {subscriptionData?.status === "active"
                          ? "Aktif"
                          : subscriptionData?.status === "free"
                          ? "Ücretsiz"
                          : subscriptionData?.status || "—"}
                      </Badge>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Karakter Kullanımı */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    Karakter Kullanımı
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSubscription ? (
                    <Skeleton className="h-8 w-32" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold">
                        {(subscriptionData?.character_count || 0).toLocaleString("tr-TR")}
                        <span className="text-sm font-normal text-muted-foreground">
                          {" "}/ {(subscriptionData?.character_limit || 0).toLocaleString("tr-TR")}
                        </span>
                      </div>
                      <Progress
                        value={
                          subscriptionData?.character_limit
                            ? (subscriptionData.character_count / subscriptionData.character_limit) * 100
                            : 0
                        }
                        className="mt-2 h-2"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        %{subscriptionData?.character_limit
                          ? ((subscriptionData.character_count / subscriptionData.character_limit) * 100).toFixed(1)
                          : "0"}{" "}
                        kullanıldı
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Ses Slotları */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Mic2 className="h-4 w-4 text-blue-500" />
                    Ses Slotları
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSubscription ? (
                    <Skeleton className="h-8 w-20" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold">
                        {subscriptionData?.voice_slots_used || 0}
                        <span className="text-sm font-normal text-muted-foreground">
                          {" "}/ {subscriptionData?.voice_limit || 0}
                        </span>
                      </div>
                      <Progress
                        value={
                          subscriptionData?.voice_limit
                            ? (subscriptionData.voice_slots_used / subscriptionData.voice_limit) * 100
                            : 0
                        }
                        className="mt-2 h-2"
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Yenilenme Tarihi */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4 text-green-500" />
                    Yenilenme Tarihi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSubscription ? (
                    <Skeleton className="h-8 w-28" />
                  ) : (
                    <>
                      <div className="text-2xl font-bold">
                        {subscriptionData?.next_character_count_reset_unix
                          ? new Date(subscriptionData.next_character_count_reset_unix * 1000).toLocaleDateString("tr-TR", {
                              day: "numeric",
                              month: "long",
                            })
                          : "—"}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {subscriptionData?.next_character_count_reset_unix
                          ? (() => {
                              const resetDate = new Date(subscriptionData.next_character_count_reset_unix * 1000);
                              const now = new Date();
                              const diffDays = Math.ceil((resetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                              return diffDays > 0 ? `${diffDays} gün kaldı` : "Bugün yenileniyor";
                            })()
                          : ""}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Detaylı Bilgiler */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Abonelik Detayları</CardTitle>
                  <CardDescription>
                    ElevenLabs hesabınızın detaylı abonelik bilgileri
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchSubscription()}
                  disabled={isLoadingSubscription}
                >
                  {isLoadingSubscription ? (
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Yenile
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingSubscription ? (
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                  </div>
                ) : subscriptionData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Sol Kolon */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Karakter Kullanımı</p>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-lg font-semibold">
                            {(subscriptionData.character_count || 0).toLocaleString("tr-TR")}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            / {(subscriptionData.character_limit || 0).toLocaleString("tr-TR")} karakter
                          </span>
                        </div>
                        <Progress
                          value={
                            subscriptionData.character_limit
                              ? (subscriptionData.character_count / subscriptionData.character_limit) * 100
                              : 0
                          }
                          className="mt-2 h-3"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Kalan: {((subscriptionData.character_limit || 0) - (subscriptionData.character_count || 0)).toLocaleString("tr-TR")} karakter
                        </p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Ses Slotları</p>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-lg font-semibold">{subscriptionData.voice_slots_used || 0}</span>
                          <span className="text-sm text-muted-foreground">/ {subscriptionData.voice_limit || 0} slot</span>
                        </div>
                      </div>

                      {subscriptionData.professional_voice_limit > 0 && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Profesyonel Ses Slotları</p>
                          <div className="flex items-baseline gap-1 mt-1">
                            <span className="text-lg font-semibold">{subscriptionData.professional_voice_slots_used || 0}</span>
                            <span className="text-sm text-muted-foreground">/ {subscriptionData.professional_voice_limit} slot</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Sağ Kolon */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Fatura Dönemi</p>
                        <p className="text-lg font-semibold mt-1">
                          {subscriptionData.billing_period === "monthly_period"
                            ? "Aylık"
                            : subscriptionData.billing_period === "annual_period"
                            ? "Yıllık"
                            : subscriptionData.billing_period === "3_month_period"
                            ? "3 Aylık"
                            : subscriptionData.billing_period === "6_month_period"
                            ? "6 Aylık"
                            : subscriptionData.billing_period || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Sonraki Yenilenme</p>
                        <p className="text-lg font-semibold mt-1">
                          {subscriptionData.next_character_count_reset_unix
                            ? new Date(subscriptionData.next_character_count_reset_unix * 1000).toLocaleDateString("tr-TR", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </p>
                      </div>

                      {subscriptionData.next_invoice && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Sonraki Fatura</p>
                          <p className="text-lg font-semibold mt-1">
                            {subscriptionData.currency === "usd" ? "$" : subscriptionData.currency === "eur" ? "€" : ""}
                            {((subscriptionData.next_invoice.amount_due_cents || 0) / 100).toFixed(2)}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-2">
                        {subscriptionData.can_use_instant_voice_cloning && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Anlık Ses Klonlama
                          </Badge>
                        )}
                        {subscriptionData.can_use_professional_voice_cloning && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            Profesyonel Klonlama
                          </Badge>
                        )}
                        {subscriptionData.can_extend_character_limit && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Limit Aşımı Aktif
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="py-12 flex flex-col items-center justify-center text-center">
                    <CreditCard className="h-12 w-12 text-muted-foreground mb-3 opacity-30" />
                    <p className="text-muted-foreground font-medium">Abonelik bilgisi bulunamadı</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Lütfen API anahtarınızın doğru ayarlandığından emin olun
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* API Log Bölümü */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>API Kullanım Logları</CardTitle>
                <CardDescription>
                  ElevenLabs API'ye yapılan isteklerin detaylı kayıtları
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ["elevenlabs-api-logs"] });
                }}
                disabled={isLoadingLogs}
              >
                {isLoadingLogs ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Yenile
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingLogs ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : apiLogs?.logs?.length > 0 ? (
                <>
                  <Table>
                    <TableCaption>
                      Toplam {apiLogs.pagination?.totalItems || 0} kayıt bulundu.
                    </TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Karakterler</TableHead>
                        <TableHead>Yanıt Süresi</TableHead>
                        <TableHead>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apiLogs.logs.map((log: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(log.createdAt).toLocaleString('tr-TR', {
                              day: '2-digit', 
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-medium">
                            {getEndpointName(log.endpoint)}
                          </TableCell>
                          <TableCell>
                            {log.characterCount > 0 ? log.characterCount.toLocaleString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>
                            {log.responseTime ? `${log.responseTime.toFixed(0)} ms` : '-'}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(log.success)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {apiLogs.pagination && apiLogs.pagination.totalPages > 1 && (
                    <div className="flex items-center justify-end space-x-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={apiLogs.pagination.currentPage <= 1}
                        onClick={() => {
                          // Önceki sayfa için backend'e istek yap
                        }}
                      >
                        Önceki
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Sayfa {apiLogs.pagination.currentPage} / {apiLogs.pagination.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={apiLogs.pagination.currentPage >= apiLogs.pagination.totalPages}
                        onClick={() => {
                          // Sonraki sayfa için backend'e istek yap
                        }}
                      >
                        Sonraki
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mb-3 opacity-30" />
                  <p className="text-muted-foreground font-medium">Henüz kayıtlı log bulunmuyor</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    ElevenLabs API'yi kullandıkça burada kayıtlar görünecektir
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ApiSettings;
